import { io, type Socket } from 'socket.io-client';

/**
 * Typed socket.io client scaffold for FieldPro Web (see ADR-0015, socket-first
 * realtime). This is infra only — it is NOT connected anywhere yet; features
 * wire it up as they land. The backend streaming channels are a hard go-live
 * dependency (see plan §8).
 */
export const REALTIME_CHANNELS = ['telemetry', 'command-ack', 'chat', 'notifications'] as const;

export type RealtimeChannel = (typeof REALTIME_CHANNELS)[number];

export type RealtimeHandler<T = unknown> = (payload: T) => void;

export type RealtimeClient = {
  /** Subscribe to a typed channel. Returns an unsubscribe function. */
  subscribe: <T = unknown>(channel: RealtimeChannel, handler: RealtimeHandler<T>) => () => void;
  /** Tear down the connection. */
  disconnect: () => void;
  /** The underlying socket (lazily created), exposed for tests/inspection. */
  readonly socket: Socket;
};

export type CreateRealtimeClientOptions = {
  /** Provides the current access token at connect/reconnect time. */
  getToken?: () => string | null | undefined;
  /**
   * Factory for the underlying socket — injectable so unit tests can pass a
   * mock without a live server. Defaults to socket.io-client's `io`.
   */
  connect?: typeof io;
};

/**
 * Lazily create a realtime client. The socket connects on first construction;
 * `getToken` (if provided) feeds the auth handshake. Channels are typed via
 * {@link RealtimeChannel}.
 */
export function createRealtimeClient(
  url: string,
  options: CreateRealtimeClientOptions = {},
): RealtimeClient {
  const { getToken, connect = io } = options;

  const socket = connect(url, {
    autoConnect: true,
    transports: ['websocket'],
    auth: (cb: (data: Record<string, unknown>) => void) => {
      const token = getToken?.();
      cb(token ? { token } : {});
    },
  });

  return {
    socket,
    subscribe<T = unknown>(channel: RealtimeChannel, handler: RealtimeHandler<T>) {
      socket.on(channel, handler as RealtimeHandler);
      return () => {
        socket.off(channel, handler as RealtimeHandler);
      };
    },
    disconnect() {
      socket.disconnect();
    },
  };
}
